/**
 * Browsing what a run produced.
 *
 * The structure on disk is already the right structure to show: a subject
 * holds `DWI/` and `PWI/`, each holds `preprocess`, `registration`,
 * `segment`/`segmentation` and `reporting`, and each of those holds the files
 * that stage wrote. So the tree is the output tree — not a curated selection
 * of it, which would mean deciding on the reader's behalf that some of their
 * results are not interesting.
 *
 * What is added is the naming: the folders get the stage names the rest of the
 * app uses, so `segment` reads as "Lesion segmentation" here and in the
 * protocol and in the run view, and a file's role is shown beside it.
 */

import { useEffect, useMemo, useState } from "react";
import { ChevronRight, FileText, Folder, FolderOpen, Image, Loader2, RefreshCw, Table2 } from "lucide-react";

import { listOutputDir, splitError } from "@/ipc/api";
import type { OutputNode } from "@/ipc/contract";
import { cn } from "@/lib/utils";
import { PathField } from "@/components/PathField";
import { Select } from "@/components/ui/primitives";
import { FileViewer } from "./FileViewer";

/** What the pipeline's folder names mean, in the words used elsewhere. */
const FOLDER_LABEL: Record<string, string> = {
  preprocess: "Preprocessing",
  registration: "Registration",
  segment: "Lesion segmentation",
  segmentation: "Lesion segmentation",
  reporting: "Reporting",
  logs: "Logs",
  DWI: "Diffusion",
  PWI: "Perfusion",
};

const KIND_ICON: Record<string, typeof FileText> = {
  nifti: Image,
  png: Image,
  csv: Table2,
  json: FileText,
  txt: FileText,
  pdf: FileText,
};

function formatBytes(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)} kB`;
  return `${n} B`;
}

function Row({
  node,
  depth,
  open,
  selected,
  onToggle,
  onSelect,
}: {
  node: OutputNode;
  depth: number;
  open: boolean;
  selected: boolean;
  onToggle: () => void;
  onSelect: () => void;
}) {
  const Icon = KIND_ICON[node.kind] ?? FileText;
  const label = node.isDir ? (FOLDER_LABEL[node.name] ?? node.name) : node.name;
  return (
    <button
      type="button"
      onClick={node.isDir ? onToggle : onSelect}
      className={cn(
        "flex w-full items-center gap-1.5 py-1 pr-2 text-left transition",
        selected ? "bg-ads-accent-soft" : "hover:bg-ads-hover",
      )}
      style={{ paddingLeft: 8 + depth * 13 }}
    >
      {node.isDir ? (
        <ChevronRight
          className={cn("h-3 w-3 shrink-0 text-neutral-400 transition", open && "rotate-90")}
        />
      ) : (
        <Icon className="h-3 w-3 shrink-0 text-neutral-300" />
      )}
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-[11.5px]",
          node.isDir ? "font-medium text-neutral-800" : "text-neutral-700",
          selected && "text-ads-accent",
        )}
        title={node.name}
      >
        {label}
      </span>
      {!node.isDir ? (
        <span className="shrink-0 text-[10px] tabular-nums text-neutral-400">
          {formatBytes(node.sizeBytes)}
        </span>
      ) : node.children?.length ? (
        <span className="shrink-0 text-[10px] tabular-nums text-neutral-300">
          {node.children.length}
        </span>
      ) : null}
    </button>
  );
}

function Tree({
  nodes,
  depth,
  keyPrefix,
  openPaths,
  selected,
  onToggle,
  onSelect,
}: {
  nodes: OutputNode[];
  depth: number;
  /** Two roots can hold the same relative path; this keeps them apart. */
  keyPrefix: string;
  openPaths: Set<string>;
  selected: string | null;
  onToggle: (relPath: string) => void;
  onSelect: (node: OutputNode) => void;
}) {
  return (
    <>
      {nodes.map((node) => {
        const open = openPaths.has(`${keyPrefix}:${node.relPath}`);
        return (
          <div key={node.relPath}>
            <Row
              node={node}
              depth={depth}
              open={open}
              selected={selected === node.relPath}
              onToggle={() => onToggle(node.relPath)}
              onSelect={() => onSelect(node)}
            />
            {open && node.children?.length ? (
              <Tree
                nodes={node.children}
                depth={depth + 1}
                keyPrefix={keyPrefix}
                openPaths={openPaths}
                selected={selected}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ) : null}
          </div>
        );
      })}
    </>
  );
}

/**
 * The files beside `relPath` in the tree — i.e. its parent folder's children.
 */
function siblingsOf(nodes: OutputNode[], relPath: string): OutputNode[] {
  const parts = relPath.split("/");
  parts.pop();
  let level = nodes;
  for (const part of parts) {
    const dir = level.find((n) => n.isDir && n.name === part);
    if (!dir?.children) return [];
    level = dir.children;
  }
  return level;
}

/**
 * Every NIfTI under the clicked file's subject + modality folder (`sub-ID/
 * DWI` or `sub-ID/PWI`), not only its own stage subfolder — so a mask from
 * one stage (e.g. `segment`) can overlay a volume from another (e.g.
 * `registration`), which same-folder-only `siblingsOf` could never offer.
 * Broader than the real structure strictly needs, on purpose: narrower is a
 * silent "you can't do that", and the reader can tell a mismatched pair apart
 * on sight. Falls back to `siblingsOf` when the path is too shallow to carry
 * a subject/modality segment (a bare folder of files with no pipeline
 * structure at all).
 */
function niftiUnderModality(nodes: OutputNode[], relPath: string): OutputNode[] {
  const parts = relPath.split("/");
  if (parts.length < 3) return siblingsOf(nodes, relPath);
  let level = nodes;
  for (const part of parts.slice(0, 2)) {
    const dir = level.find((n) => n.isDir && n.name === part);
    if (!dir?.children) return siblingsOf(nodes, relPath);
    level = dir.children;
  }
  const out: OutputNode[] = [];
  const walk = (ns: OutputNode[]) => {
    for (const n of ns) {
      if (n.isDir) walk(n.children ?? []);
      else if (n.kind === "nifti") out.push(n);
    }
  };
  walk(level);
  return out;
}

type ModalityFilter = "all" | "DWI" | "PWI";
type StageFilter = "all" | "preprocess" | "registration" | "segment" | "reporting";

const STAGE_LABEL: Record<Exclude<StageFilter, "all">, string> = {
  preprocess: "Preprocessing",
  registration: "Registration",
  segment: "Lesion segmentation",
  reporting: "Reporting",
};

function stageOf(name: string): Exclude<StageFilter, "all"> | null {
  if (name === "segment" || name === "segmentation") return "segment";
  if (name === "preprocess" || name === "registration" || name === "reporting") return name;
  return null;
}

interface FlatHit {
  node: OutputNode;
  subject: string | null;
  modality: ModalityFilter | null;
  stage: Exclude<StageFilter, "all"> | null;
}

/**
 * A fast, scoped path to "this subject's registration-stage outputs" without
 * expanding the tree node by node — walks the whole subject/modality/stage
 * structure once and keeps only the files matching both filters. Assumes the
 * real pipeline's own layout (subject / DWI|PWI / stage / file); a folder
 * that doesn't look like that just yields no hits, which an empty-state
 * message below explains rather than silently mis-filtering.
 */
function flattenFiltered(nodes: OutputNode[], modalityFilter: ModalityFilter, stageFilter: StageFilter): FlatHit[] {
  const out: FlatHit[] = [];
  const walk = (
    ns: OutputNode[],
    depth: number,
    subject: string | null,
    modality: ModalityFilter | null,
    stage: Exclude<StageFilter, "all"> | null,
  ) => {
    for (const n of ns) {
      let nextSubject = subject;
      let nextModality = modality;
      let nextStage = stage;
      if (depth === 0) nextSubject = n.name;
      else if (depth === 1 && (n.name === "DWI" || n.name === "PWI")) nextModality = n.name;
      else if (depth === 2) nextStage = stageOf(n.name);
      if (n.isDir) {
        walk(n.children ?? [], depth + 1, nextSubject, nextModality, nextStage);
        continue;
      }
      if (modalityFilter !== "all" && nextModality !== modalityFilter) continue;
      if (stageFilter !== "all" && nextStage !== stageFilter) continue;
      out.push({ node: n, subject: nextSubject, modality: nextModality, stage: nextStage });
    }
  };
  walk(nodes, 0, null, null, null);
  return out;
}

/** A top-level entry in the tree: a project, or a bare output folder. */
export interface OutputSource {
  id: string;
  label: string;
  /** The line under the name — a question, or the path. */
  sublabel?: string;
  path: string;
  kind: "project" | "root";
}

/**
 * The tree starts above the output folder.
 *
 * Browsing used to need a root chosen somewhere else, reached through a small
 * text link on a summary page — findable once and not again. The hierarchy on
 * disk already runs project, output folder, subject, stage, file, so that is
 * the hierarchy: open a project and its results are under it, with the folders
 * that belong to no project below.
 *
 * Roots are read when they are opened, not up front: a scan of every output
 * folder on the machine to draw a list nobody has expanded is a slow start for
 * no one's benefit.
 */
export function OutputBrowser({
  sources,
  onOpenVolume,
}: {
  sources: OutputSource[];
  onOpenVolume?: (path: string) => void;
}) {
  /** Folders opened by typing a path, kept beside the ones the app knows. */
  const [added, setAdded] = useState<OutputSource[]>([]);
  const [trees, setTrees] = useState<Record<string, OutputNode[]>>({});
  const [loading, setLoading] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [openRoots, setOpenRoots] = useState<Set<string>>(new Set());
  const [openPaths, setOpenPaths] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<{ source: OutputSource; node: OutputNode } | null>(null);
  const [modalityFilter, setModalityFilter] = useState<ModalityFilter>("all");
  const [stageFilter, setStageFilter] = useState<StageFilter>("all");
  const filtering = modalityFilter !== "all" || stageFilter !== "all";

  const read = async (source: OutputSource) => {
    setLoading((s) => new Set(s).add(source.id));
    try {
      const { tree } = await listOutputDir(source.path);
      setTrees((t) => ({ ...t, [source.id]: tree }));
      setErrors(({ [source.id]: _drop, ...rest }) => rest);
    } catch (e) {
      setTrees((t) => ({ ...t, [source.id]: [] }));
      setErrors((x) => ({ ...x, [source.id]: splitError(e).message }));
    } finally {
      setLoading((s) => {
        const next = new Set(s);
        next.delete(source.id);
        return next;
      });
    }
  };

  const all = useMemo(() => [...sources, ...added], [sources, added]);

  // One source is no choice at all, so it opens itself.
  useEffect(() => {
    if (all.length === 1 && !openRoots.has(all[0].id)) {
      setOpenRoots(new Set([all[0].id]));
      void read(all[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all]);

  /** Open a folder the app has no record of — an archive, another machine. */
  const addRoot = (path: string) => {
    const id = `typed:${path}`;
    const source: OutputSource = {
      id,
      label: path.split(/[\\/]/).filter(Boolean).pop() ?? path,
      sublabel: path,
      path,
      kind: "root",
    };
    setAdded((prev) => (prev.some((s) => s.id === id) ? prev : [...prev, source]));
    setOpenRoots((prev) => new Set(prev).add(id));
    void read(source);
  };

  const toggleRoot = (source: OutputSource) => {
    setOpenRoots((prev) => {
      const next = new Set(prev);
      if (next.has(source.id)) {
        next.delete(source.id);
      } else {
        next.add(source.id);
        if (!trees[source.id]) void read(source);
      }
      return next;
    });
  };

  const toggle = (key: string) =>
    setOpenPaths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const absPath = useMemo(
    () =>
      selected
        ? `${selected.source.path.replace(/[\\/]+$/, "")}/${selected.node.relPath}`
        : null,
    [selected],
  );

  // Every source's filtered hits, flattened across subjects — computed once
  // here rather than per-source in the render below, since both the empty
  // state and the list itself need the combined count.
  const filteredHits = useMemo(() => {
    if (!filtering) return [];
    return all.flatMap((source) =>
      flattenFiltered(trees[source.id] ?? [], modalityFilter, stageFilter).map((hit) => ({ source, hit })),
    );
  }, [all, trees, filtering, modalityFilter, stageFilter]);

  return (
    <div className="flex h-full min-h-0">
      <aside className="flex w-[300px] shrink-0 flex-col border-r border-ads-border bg-ads-surface">
        {/* A fast, scoped path to one subject's stage outputs — "show me the
            registration files" — without expanding the tree node by node.
            Leaving both at "All" falls straight back to the plain tree. */}
        <div className="flex shrink-0 items-center gap-1.5 border-b border-ads-border px-2 py-1.5">
          <Select
            value={modalityFilter}
            onChange={(e) => setModalityFilter(e.target.value as ModalityFilter)}
            className="h-7 text-[11px]"
          >
            <option value="all">All modalities</option>
            <option value="DWI">DWI</option>
            <option value="PWI">PWI</option>
          </Select>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            className="h-7 text-[11px]"
          >
            <option value="all">All stages</option>
            {(Object.keys(STAGE_LABEL) as (keyof typeof STAGE_LABEL)[]).map((id) => (
              <option key={id} value={id}>
                {STAGE_LABEL[id]}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-h-0 flex-1 overflow-auto py-1">
          {all.length === 0 ? (
            <p className="px-3 py-4 text-[11px] leading-relaxed text-neutral-500">
              No output folders yet. They appear here once something has run — or open one below.
            </p>
          ) : filtering ? (
            filteredHits.length === 0 ? (
              <p className="px-3 py-4 text-[11px] leading-relaxed text-neutral-500">
                No files match that filter in the open folders. A folder that doesn't follow the
                sub-ID/DWI|PWI/stage layout (a one-off tool run, say) never matches one.
              </p>
            ) : (
              filteredHits.map(({ source, hit }) => (
                <button
                  key={`${source.id}:${hit.node.relPath}`}
                  type="button"
                  onClick={() => setSelected({ source, node: hit.node })}
                  className={cn(
                    "flex w-full items-center gap-1.5 px-3 py-1.5 text-left",
                    selected?.source.id === source.id && selected.node.relPath === hit.node.relPath
                      ? "bg-ads-accent-soft"
                      : "hover:bg-ads-hover",
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11.5px] text-neutral-800">{hit.node.name}</span>
                    <span className="block truncate text-[10px] text-neutral-400">
                      {[source.label, hit.subject, hit.modality, hit.stage ? STAGE_LABEL[hit.stage] : null]
                        .filter(Boolean)
                        .join(" › ")}
                    </span>
                  </span>
                  <span className="shrink-0 text-[10px] tabular-nums text-neutral-400">
                    {formatBytes(hit.node.sizeBytes)}
                  </span>
                </button>
              ))
            )
          ) : (
            all.map((source) => {
            const open = openRoots.has(source.id);
            const tree = trees[source.id] ?? [];
            const Icon = source.kind === "project" ? FolderOpen : Folder;
            return (
              <div key={source.id}>
                <button
                  type="button"
                  onClick={() => toggleRoot(source)}
                  className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left hover:bg-ads-hover"
                >
                  <ChevronRight
                    className={cn("h-3 w-3 shrink-0 text-neutral-400 transition", open && "rotate-90")}
                  />
                  <Icon className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11.5px] font-semibold text-neutral-800">
                      {source.label}
                    </span>
                    {source.sublabel ? (
                      <span
                        className="block truncate font-mono text-[9.5px] text-neutral-400"
                        title={source.path}
                      >
                        {source.sublabel}
                      </span>
                    ) : null}
                  </span>
                  {loading.has(source.id) ? (
                    <Loader2 className="h-3 w-3 shrink-0 animate-spin text-neutral-400" />
                  ) : open ? (
                    <span
                      role="button"
                      tabIndex={-1}
                      aria-label="Re-read"
                      onClick={(e) => {
                        e.stopPropagation();
                        void read(source);
                      }}
                      className="shrink-0 text-neutral-300 hover:text-neutral-600"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </span>
                  ) : null}
                </button>

                {open ? (
                  errors[source.id] ? (
                    <p className="px-3 py-2 text-[10.5px] leading-relaxed text-red-700">
                      {errors[source.id]}
                    </p>
                  ) : tree.length === 0 && !loading.has(source.id) ? (
                    <p className="px-3 py-2 pl-8 text-[10.5px] text-neutral-400">Nothing here yet.</p>
                  ) : (
                    <Tree
                      nodes={tree}
                      depth={1}
                      keyPrefix={source.id}
                      openPaths={openPaths}
                      selected={
                        selected?.source.id === source.id ? selected.node.relPath : null
                      }
                      onToggle={(rel) => toggle(`${source.id}:${rel}`)}
                      onSelect={(node) => setSelected({ source, node })}
                    />
                  )
                ) : null}
              </div>
            );
          }))}
        </div>
        <div className="shrink-0 border-t border-ads-border px-2 py-2">
          <PathField
            title="Open an output folder"
            placeholder="Open another folder by path"
            onSubmit={addRoot}
          />
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col bg-ads-bg">
        {selected ? (
          <div className="flex shrink-0 items-baseline gap-2 border-b border-ads-border bg-ads-surface px-4 py-2">
            <span className="truncate text-[12.5px] font-semibold text-neutral-900">
              {selected.node.name}
            </span>
            <span className="shrink-0 text-[10.5px] uppercase tracking-wide text-neutral-400">
              {selected.node.kind}
            </span>
            <span className="flex-1" />
            <span className="shrink-0 text-[10.5px] tabular-nums text-neutral-400">
              {formatBytes(selected.node.sizeBytes)}
            </span>
          </div>
        ) : null}
        <div className="min-h-0 flex-1">
          <FileViewer
            node={selected?.node ?? null}
            absPath={absPath}
            onOpenVolume={onOpenVolume}
            siblingVolumes={
              selected ? niftiUnderModality(trees[selected.source.id] ?? [], selected.node.relPath) : undefined
            }
            sourceRoot={selected?.source.path}
          />
        </div>
      </section>
    </div>
  );
}
